import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, useAuth } from '../App';
import { X, Dog, Cat, MapPin, ArrowRight } from 'lucide-react';
import { PawPrint } from './ParallaxBackground';

export default function OnboardingModal({ onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [species, setSpecies] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    apiFetch('/api/nodes').then(setNodes).catch(() => {});
  }, []);

  const stores = nodes
    .filter(n => n.type === 'STORE' && !n.comingSoon)
    .sort((a, b) => (a.slug === 'bandra' ? 1 : b.slug === 'bandra' ? -1 : 0));

  const handleContinue = async () => {
    if (user && (species || selectedNode)) {
      try {
        await apiFetch('/api/auth/preferences', {
          method: 'PUT',
          body: JSON.stringify({ preferredSpecies: species, preferredNodeId: selectedNode }),
        });
      } catch (e) {
        console.error('Failed to save preferences:', e);
      }
    }
    if (species) localStorage.setItem('petfolk_species', species);
    if (selectedNode) localStorage.setItem('petfolk_node', selectedNode);
    onClose();
    if (selectedNode) {
      const node = nodes.find(n => n.id === selectedNode);
      if (node) navigate(`/${node.slug}`);
    }
  };

  return (
    <div className="modal-overlay" data-testid="onboarding-modal">
      <div className="petfolk-card w-full max-w-lg mx-4 p-8 relative animate-slide-up" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/5" data-testid="onboarding-close-btn">
          <X size={20} />
        </button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-petfolk-orange to-petfolk-purple flex items-center justify-center mx-auto mb-4">
            <PawPrint size={28} color="white" />
          </div>
          <h2 className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Welcome to Petfolk!
          </h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Select your pet to personalize your experience
          </p>
        </div>

        {/* Pet Selection */}
        <div className="flex gap-4 justify-center mb-8">
          <button
            data-testid="select-dog-btn"
            onClick={() => setSpecies('DOG')}
            className={`flex flex-col items-center gap-3 p-6 rounded-2xl border-2 transition-all duration-200 w-36 ${
              species === 'DOG' ? 'border-petfolk-orange bg-orange-50 dark:bg-orange-900/20 scale-105' : 'border-transparent hover:border-petfolk-orange/30'
            }`}
            style={{ background: species !== 'DOG' ? 'var(--bg-card)' : undefined }}
          >
            <span className="text-5xl">🐕</span>
            <span className="font-outfit font-semibold" style={{ color: 'var(--text-primary)' }}>Dog</span>
          </button>
          <button
            data-testid="select-cat-btn"
            onClick={() => setSpecies('CAT')}
            className={`flex flex-col items-center gap-3 p-6 rounded-2xl border-2 transition-all duration-200 w-36 ${
              species === 'CAT' ? 'border-petfolk-purple bg-purple-50 dark:bg-purple-900/20 scale-105' : 'border-transparent hover:border-petfolk-purple/30'
            }`}
            style={{ background: species !== 'CAT' ? 'var(--bg-card)' : undefined }}
          >
            <span className="text-5xl">🐈</span>
            <span className="font-outfit font-semibold" style={{ color: 'var(--text-primary)' }}>Cat</span>
          </button>
        </div>

        {/* Location Selection */}
        <div className="mb-6">
          <p className="font-outfit font-semibold text-center mb-4" style={{ color: 'var(--text-primary)' }}>
            Where would you like to book?
          </p>
          <div className="grid grid-cols-2 gap-3">
            {stores.map(node => (
              <button
                key={node.id}
                data-testid={`select-node-${node.slug}`}
                onClick={() => setSelectedNode(node.id)}
                className={`relative overflow-hidden rounded-2xl border-2 transition-all duration-200 ${
                  selectedNode === node.id ? 'border-petfolk-orange scale-[1.02]' : 'border-transparent hover:border-petfolk-orange/30'
                }`}
              >
                <img
                  src={node.heroImageUrl}
                  alt={node.displayName}
                  className="w-full h-24 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 text-left">
                  <p className="text-white font-outfit font-semibold text-sm">{node.displayName}</p>
                  <p className="text-white/70 text-xs flex items-center gap-1">
                    <MapPin size={10} />{node.localName} - {node.cityName}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <button
            data-testid="onboarding-skip-btn"
            onClick={onClose}
            className="btn-outline flex-1 justify-center text-sm"
          >
            Skip for now
          </button>
          <button
            data-testid="onboarding-continue-btn"
            onClick={handleContinue}
            className="btn-primary flex-1 justify-center text-sm"
          >
            Continue <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
